import * as React from 'react';

import { ChatMessageRole } from '@rematter/frontend-lib-api-client/grpc/assistant/v1/assistant_pb';
import { ChatMessageCreatedEvent } from '@rematter/frontend-lib-api-client/grpc/assistant/v1/events_pb';
import { BadgeField, Col, FieldDisplay, IDField } from '@rematter/ui-web';

import { useTranslations } from '../../../i18n/useTranslations';
import { chatMessageRoleColors } from '../enumLabels';

type ChatMessageCreatedDetailProps = {
  event: ChatMessageCreatedEvent;
};

//
// ⚛️ ⚛️ ⚛️ CHAT MESSAGE CREATED DETAIL ⚛️ ⚛️ ⚛️
//
export const ChatMessageCreatedDetail = ({
  event,
}: ChatMessageCreatedDetailProps) => {
  const t = useTranslations();

  const message = event.chatMessage;

  // Memoized values
  const chatMessageRoleLabels = React.useMemo<Record<ChatMessageRole, string>>(
    () => ({
      [ChatMessageRole.UNSPECIFIED]: t.logs.chatMessageRole.unspecified,
      [ChatMessageRole.USER]: t.logs.chatMessageRole.user,
      [ChatMessageRole.ASSISTANT]: t.logs.chatMessageRole.assistant,
      [ChatMessageRole.SYSTEM]: t.logs.chatMessageRole.system,
    }),
    [t],
  );

  if (!message) {
    return (
      <Col className="@ChatMessageCreatedDetail items-center justify-center p-4 text-neutral/40">
        <span>{t.logs.noDataAvailable}</span>
      </Col>
    );
  }

  return (
    <Col className="@ChatMessageCreatedDetail gap-3" gap3>
      <Col className="rounded-md border border-neutral/10 bg-white p-3" gap2>
        <span className="text-xs font-medium text-neutral/60">
          {t.logs.messageDetails}
        </span>
        <div className="grid grid-cols-2 gap-x-4 gap-y-1">
          <IDField label={t.logs.fields.id} value={message.id?.value} />
          <IDField label={t.logs.fields.chatId} value={message.chatId?.value} />
          <BadgeField
            label={t.logs.fields.role}
            value={chatMessageRoleLabels[message.role]}
            color={chatMessageRoleColors[message.role]}
          />
          <IDField
            label={t.logs.fields.agentId}
            value={message.agentId?.value}
          />
          <FieldDisplay
            label={t.logs.fields.partsCount}
            value={String(message.parts?.length ?? 0)}
          />
        </div>
      </Col>

      {message.parts && message.parts.length > 0 && (
        <Col className="rounded-md border border-neutral/10 bg-white p-3" gap2>
          <span className="text-xs font-medium text-neutral/60">
            {t.logs.messageParts} ({message.parts.length})
          </span>
          <Col gap2>
            {message.parts.map((part, index) => (
              <Col
                key={part.id?.value ?? index}
                className="rounded border border-neutral/5 bg-neutral/2 p-2"
              >
                <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm">
                  <FieldDisplay
                    label={t.logs.fields.sequence}
                    value={String(part.sequence)}
                  />
                  <FieldDisplay
                    label={t.logs.fields.type}
                    value={part.partData.case ?? t.logs.fields.unknown}
                  />
                  {part.partData.case === 'text' && (
                    <Col className="col-span-2">
                      <span className="text-xs text-neutral/50">
                        {t.logs.fields.content}
                      </span>
                      <span className="text-sm">
                        {part.partData.value.text?.substring(0, 200)}
                        {(part.partData.value.text?.length ?? 0) > 200
                          ? '...'
                          : ''}
                      </span>
                    </Col>
                  )}
                  {part.partData.case === 'toolCall' && (
                    <FieldDisplay
                      label={t.logs.fields.toolName}
                      value={part.partData.value.toolName}
                    />
                  )}
                </div>
              </Col>
            ))}
          </Col>
        </Col>
      )}
    </Col>
  );
};
